// `excel_write_block` — layout and pre-write checks of a model block (domain #32 / D3). Pure: no Office.js.
//
// A block is a title (optional), a label column, a unit column (when any row has a unit) and, per row,
// ONE R1C1 pattern or per-period constants over the timeline axis. One call = one card = one undo.
import { colToLetter, letterToCol, parseCell, r1c1Problem } from './a1';
import { legacyProblems } from './legacy';
import type { BlockRow, CellValue, WriteBlockInput } from '../../types/excel';

export interface BlockLayoutRow {
  index: number;
  /** Absolute worksheet row. */
  row: number;
  label: string;
  unit: string | null;
  role: BlockRow['role'];
  formula: string | null;
  values: CellValue[] | null;
  fmt: string | null;
  /** Justification of a normally forbidden function — becomes a note on the label cell. */
  justification: string | null;
}

export interface BlockLayout {
  sheet: string;
  titleCell: string | null;
  /** First row written (the title row when there is a title, else the first data row). */
  topRow: number;
  firstRow: number;
  lastRow: number;
  labelCol: number;
  unitCol: number | null;
  firstCol: number;
  lastCol: number;
  periods: number;
  rows: BlockLayoutRow[];
  /** Whole rectangle touched: label column → last axis column, top row → last row. */
  targetAddress: string;
  /** Axis rectangle only (formulas / values), for the post-write verification. */
  axisAddress: string;
  rowCount: number;
}

const FORMULA_ROLES = new Set(['calc', 'total', 'check']);
const ERROR_RE = /^#(REF!|DIV\/0!|VALUE!|NAME\?|N\/A|NUM!|NULL!|SPILL!|CALC!)$/;

export function cellA1(col: number, row: number): string {
  return `${colToLetter(col)}${row}`;
}

/** Pre-write checks (mirrors the backend validation, plus what only the client can see). */
export function validateBlock(input: WriteBlockInput): string[] {
  const problems: string[] = [];
  let anchor: { row: number; col: number } | null = null;
  try {
    anchor = parseCell(input.anchor);
  } catch {
    problems.push(`anchor '${input.anchor}' is not a cell`);
  }
  if (!Array.isArray(input.rows) || !input.rows.length) problems.push('rows is empty');
  if ((input.rows?.length ?? 0) > 60) problems.push('at most 60 rows per block');
  if (input.title && anchor && anchor.row < 2) problems.push('anchor must be on row 2 or below when a title is given (the title goes one row above)');
  let periods = 1;
  if (input.axis) {
    let f = 0;
    let l = 0;
    try {
      f = letterToCol(String(input.axis.first_col).toUpperCase());
      l = letterToCol(String(input.axis.last_col).toUpperCase());
    } catch {
      problems.push('axis columns are plain column letters');
    }
    if (f && l) {
      if (l < f) problems.push('axis.last_col is left of axis.first_col');
      periods = l - f + 1;
      const hasUnit = (input.rows ?? []).some((r) => r.unit);
      if (anchor && f <= anchor.col + (hasUnit ? 1 : 0)) problems.push('the axis must start right of the label' + (hasUnit ? ' and unit columns' : ' column'));
    }
  }
  for (const r of input.rows ?? []) {
    const hasF = typeof r.formula_r1c1 === 'string' && r.formula_r1c1.length > 0;
    const hasV = Array.isArray(r.values);
    if (FORMULA_ROLES.has(r.role)) {
      if (!hasF || hasV) problems.push(`row '${r.label}': a ${r.role} row carries exactly one formula_r1c1`);
    } else if (r.role === 'input') {
      if (hasF || !hasV) problems.push(`row '${r.label}': an input row carries values, never a formula`);
    } else if (hasF && hasV) problems.push(`row '${r.label}': formula_r1c1 or values, not both`);
    if (hasF) {
      const p = r1c1Problem(r.formula_r1c1 as string);
      if (p) problems.push(`row '${r.label}': ${p}`);
      const lint = legacyProblems(r.formula_r1c1 as string, { calcRow: r.role === 'calc' || r.role === 'total', justification: r.justification ?? null });
      for (const q of lint) problems.push(`row '${r.label}': ${q}`);
    }
    if (hasV) {
      const n = (r.values as CellValue[]).length;
      if (n !== 1 && n !== periods) problems.push(`row '${r.label}': ${n} values for ${periods} period(s)`);
      if ((r.values as CellValue[]).some((v) => typeof v === 'string' && v.startsWith('='))) problems.push(`row '${r.label}': values must not contain formulas`);
    }
  }
  return problems;
}

/** Deterministic placement of the block from its anchor (call after `validateBlock`). */
export function layoutBlock(input: WriteBlockInput): BlockLayout {
  const anchor = parseCell(input.anchor);
  const rows = input.rows;
  const hasUnit = rows.some((r) => r.unit);
  const labelCol = anchor.col;
  const unitCol = hasUnit ? labelCol + 1 : null;
  let firstCol: number;
  let lastCol: number;
  if (input.axis) {
    firstCol = letterToCol(String(input.axis.first_col).toUpperCase());
    lastCol = letterToCol(String(input.axis.last_col).toUpperCase());
  } else {
    firstCol = (unitCol ?? labelCol) + 1;
    lastCol = firstCol;
  }
  const firstRow = anchor.row;
  const lastRow = firstRow + rows.length - 1;
  const topRow = input.title ? firstRow - 1 : firstRow;
  const laid: BlockLayoutRow[] = rows.map((r, i) => ({
    index: i,
    row: firstRow + i,
    label: r.label,
    unit: r.unit ?? null,
    role: r.role,
    formula: typeof r.formula_r1c1 === 'string' && r.formula_r1c1 ? r.formula_r1c1 : null,
    values: Array.isArray(r.values) ? r.values : null,
    fmt: r.number_format ?? null,
    justification: typeof r.justification === 'string' && r.justification.trim() ? r.justification.trim() : null,
  }));
  return {
    sheet: input.sheet,
    titleCell: input.title ? cellA1(labelCol, topRow) : null,
    topRow,
    firstRow,
    lastRow,
    labelCol,
    unitCol,
    firstCol,
    lastCol,
    periods: lastCol - firstCol + 1,
    rows: laid,
    targetAddress: `${cellA1(labelCol, topRow)}:${cellA1(lastCol, lastRow)}`,
    axisAddress: `${cellA1(firstCol, firstRow)}:${cellA1(lastCol, lastRow)}`,
    rowCount: lastRow - topRow + 1,
  };
}

/** Error cells in the axis rectangle after the write. */
export function errorCells(values: CellValue[][], origin: { row: number; col: number }, max = 20): string[] {
  const out: string[] = [];
  values.forEach((rowVals, r) => {
    rowVals.forEach((v, c) => {
      if (typeof v === 'string' && ERROR_RE.test(v) && out.length < max) out.push(`${cellA1(origin.col + c, origin.row + r)} ${v}`);
    });
  });
  return out;
}
